import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { FiClock, FiExternalLink, FiHelpCircle, FiLoader } from 'react-icons/fi';
import CopyButton from '../components/transaction/CopyButton';
import TxDetailsPageHeader from '../components/transaction/TxDetailsPageHeader';
import SponsoredSection from '../components/SponsoredSection';
import { fetchPrcBlock } from '../api/transactions';
import { formatRelativeTime, formatUtcTimestamp } from '../utils/formatTime';

function PrcBlockDetailsView({ number }) {
  const [display, setDisplay] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);

  useEffect(() => {
    const normalized = String(number || '').trim();
    if (!/^[0-9]+$/.test(normalized)) {
      setLoading(false);
      setError('Invalid block number.');
      return undefined;
    }

    let cancelled = false;
    setLoading(true);
    setError(null);

    fetchPrcBlock(normalized, page)
      .then((result) => {
        if (!cancelled) {
          setDisplay(result?.display || result);
        }
      })
      .catch((err) => {
        if (!cancelled) {
          setError(err.response?.data?.message || err.message || 'Block not found.');
        }
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false);
        }
      });

    return () => { cancelled = true; };
  }, [number, page]);

  if (loading) {
    return (
      <main className="sections eth-tx-page">
        <TxDetailsPageHeader title="Block Details" />
        <div className="stx-skeleton stx-skeleton-panel" />
      </main>
    );
  }

  if (error || !display) {
    return (
      <main className="sections eth-tx-page">
        <TxDetailsPageHeader title="Block Details" />
        <div className="stx-error">
          <p>{error || 'Block not found.'}</p>
          <Link to="/" className="eth-tx-link">Back to home</Link>
        </div>
      </main>
    );
  }

  const pagination = display.pagination;

  return (
    <main className="sections eth-tx-page">
      <SponsoredSection />
      <TxDetailsPageHeader title="Block Details" />
      <p className="eth-tx-subtitle">PRC Mainnet block {display.blockNumber}</p>

      <section className="eth-tx-card">
        <div className="eth-tx-row">
          <div className="eth-tx-row__label"><FiHelpCircle aria-hidden /> Block:</div>
          <div className="eth-tx-row__value">
            <span className="eth-tx-link eth-tx-link--bold">{display.blockNumber}</span>
            <CopyButton text={String(display.blockNumber)} className="eth-tx-copy" />
          </div>
        </div>

        {display.blockHash ? (
          <div className="eth-tx-row">
            <div className="eth-tx-row__label">Block Hash:</div>
            <div className="eth-tx-row__value">
              <code className="eth-tx-mono">{display.blockHash}</code>
              <CopyButton text={display.blockHash} className="eth-tx-copy" />
            </div>
          </div>
        ) : null}

        <div className="eth-tx-row">
          <div className="eth-tx-row__label"><FiHelpCircle aria-hidden /> Timestamp:</div>
          <div className="eth-tx-row__value eth-tx-inline">
            <FiClock aria-hidden />
            {formatRelativeTime(display.blockTime)}
            <span className="eth-tx-time-sep">|</span>
            {formatUtcTimestamp(display.blockTime)}
          </div>
        </div>

        <div className="eth-tx-row">
          <div className="eth-tx-row__label">Transactions:</div>
          <div className="eth-tx-row__value">{display.transactionCount?.toLocaleString()}</div>
        </div>

        {display.validator ? (
          <div className="eth-tx-row">
            <div className="eth-tx-row__label">Validator:</div>
            <div className="eth-tx-row__value">{display.validator}</div>
          </div>
        ) : null}
      </section>

      {display.transactions?.length > 0 ? (
        <section className="eth-tx-card">
          <h2 className="stx-card-heading">Transactions</h2>
          <div className="stx-block-tx-list">
            {display.transactions.map((tx) => (
              <Link
                key={tx.hash}
                to={`/tx/${encodeURIComponent(tx.hash)}`}
                className="stx-block-tx-item"
              >
                <span className="stx-block-tx-hash">{tx.displayHash}</span>
              </Link>
            ))}
          </div>

          {pagination && pagination.last_page > 1 ? (
            <div className="stx-block-pagination">
              <button type="button" disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))}>
                Prev
              </button>
              <span>Page {page} of {pagination.last_page}</span>
              <button type="button" disabled={page >= pagination.last_page} onClick={() => setPage((p) => p + 1)}>
                Next
              </button>
            </div>
          ) : null}
        </section>
      ) : null}
    </main>
  );
}

export default PrcBlockDetailsView;
